import { type FC, useEffect, useState } from "react";
import { Dialog, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useCreatePromocodeMutation } from "@/partner/entities/promocodes";
import type { CreatePromocodeInputType } from "@/types/graphql/partner.generated";
import { PromocodeForm } from "./PromocodeForm";

interface CreatePromocodeDialogProps {
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
}

/**
 * Диалог создания промокода (référence: PromocodeForm.js в режиме create).
 * Обработка дубликата кода: mongo кидает E11000 на уникальном индексе code —
 * подставляем код в i18n-сообщение `promocodes.dialog.codeError`, для чего
 * запоминаем последний отправленный code (форма его не хранит наружу
 * отдельно от input).
 */
export const CreatePromocodeDialog: FC<CreatePromocodeDialogProps> = ({
  open,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastSubmittedCode, setLastSubmittedCode] = useState("");

  // Спека §2.7: ошибка не должна переживать закрытие/повторное открытие
  // диалога — компонент смонтирован всегда (`open` только у `Dialog`).
  useEffect(() => {
    setErrorMessage(null);
  }, [open]);

  const [createPromocode, { loading }] = useCreatePromocodeMutation({
    refetchQueries: ["PromocodesList"],
    onCompleted: () => {
      setErrorMessage(null);
      enqueueSnackbar(t("promocodes.dialog.created"), { variant: "success" });
      onCompleted();
    },
    onError: (error) => {
      const message = error.graphQLErrors.some((graphQLError) =>
        graphQLError.message.includes("E11000")
      )
        ? t("promocodes.dialog.codeError", { code: lastSubmittedCode })
        : error.message;
      setErrorMessage(message);
    },
  });

  const handleSubmit = (input: CreatePromocodeInputType) => {
    setLastSubmittedCode(input.code);
    void createPromocode({ variables: { input } });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="body"
      fullScreen={fullScreen}
    >
      <PromocodeForm
        title={t("promocodes.dialog.createTitle")}
        submitLabel={t("common.actions.create")}
        loading={loading}
        error={errorMessage}
        onSubmit={handleSubmit}
        onClose={onClose}
      />
    </Dialog>
  );
};
